import { API_ROUTES } from "definitions/constants/routeConstants";
import { fromActionPrefixId } from "features/Resources/Shared/helpers";
import { PrefixId } from "features/Resources/Shared/models";
import { fromTemplatePrefixId } from "features/Resources/SharedTemplate/helpers";
import { TemplatePrefixId } from "features/Resources/SharedTemplate/models";
import { generatePath as caminoGeneratePath } from "helpers/url/pages/v1";
import { IssueDetail, IssueSummary } from "models/issue";
import { NewHireJourney, NewHireJourneyId } from "models/newHire";
import { generatePath } from "react-router";

// Resources
export function apiEmailById(emailId: string) {
  return caminoGeneratePath({ path: API_ROUTES.emails.show, params: { id: emailId || "" } });
}

export function apiEmailTemplateById(emailTemplateId: string) {
  return caminoGeneratePath({ path: API_ROUTES.emailTemplates.show, params: { id: emailTemplateId || "" } });
}

export function apiMessageById(messageId: string) {
  return caminoGeneratePath({ path: API_ROUTES.messages.show, params: { id: messageId || "" } });
}

export function apiMessageTemplateById(messageTemplateId: string) {
  return caminoGeneratePath({ path: API_ROUTES.messageTemplates.show, params: { id: messageTemplateId || "" } });
}

export function apiMeetingById(meetingId: string) {
  return caminoGeneratePath({ path: API_ROUTES.meetings.show, params: { id: meetingId || "" } });
}

export function apiMeetingTemplateById(meetingTemplateId: string) {
  return caminoGeneratePath({ path: API_ROUTES.meetingTemplates.show, params: { id: meetingTemplateId || "" } });
}

export function apiTaskById(taskId: string | undefined) {
  return caminoGeneratePath({ path: API_ROUTES.tasks.show, params: { id: taskId || "" } });
}

export function apiTaskTemplateById(taskTemplateId: string) {
  return caminoGeneratePath({ path: API_ROUTES.taskTemplates.show, params: { id: taskTemplateId || "" } });
}

export function apiCheckpointById(checkpointId: string) {
  return caminoGeneratePath({ path: API_ROUTES.checkpoints.show, params: { id: checkpointId || "" } });
}

export function apiCheckpointTemplateById(checkpointTemplateId: string) {
  return caminoGeneratePath({ path: API_ROUTES.checkpointTemplates.show, params: { id: checkpointTemplateId || "" } });
}

// utils/apiPaths.ts

export function templateApiPathByPrefixId(templatePrefixId: TemplatePrefixId | undefined) {
  if (!templatePrefixId) {
    return "";
  }
  const { templateType, id: templateId } = fromTemplatePrefixId(templatePrefixId);

  const templatePathMap = {
    email_template: (id: string) => apiEmailTemplateById(id),
    meeting_template: (id: string) => apiMeetingTemplateById(id),
    message_template: (id: string) => apiMessageTemplateById(id),
    task_template: (id: string) => apiTaskTemplateById(id),
    checkpoint_template: (id: string) => apiCheckpointTemplateById(id),
  };

  const getPath = templatePathMap[templateType as keyof typeof templatePathMap];
  if (!getPath) throw new Error(`Error calling templateApiPathByPrefixId - Invalid template type: ${templateType}`);

  return getPath(templateId as string);
}

export function resourceApiPathByPrefixId(prefixId: PrefixId | undefined) {
  if (!prefixId) {
    return "";
  }
  const { resourceType: actionType, id: actionId } = fromActionPrefixId(prefixId);

  const actionPathMap = {
    email: (id: string) => apiEmailById(id),
    meeting: (id: string) => apiMeetingById(id),
    message: (id: string) => apiMessageById(id),
    task: (id: string) => apiTaskById(id),
    checkpoint: (id: string) => apiCheckpointById(id),
  };

  const getPath = actionPathMap[actionType as keyof typeof actionPathMap];
  if (!getPath) throw new Error(`Error calling resourceApiPathByPrefixId - Invalid action type: ${actionType}`);

  return getPath(actionId as string);
}

export function apiMessageServiceChannelPathById(messagingChannelId: string) {
  return caminoGeneratePath({
    path: API_ROUTES.messageServices.channels.show,
    params: { id: messagingChannelId || "" },
  });
}

// new-hire-journeys

export function apiNewHireListPath(queryString?: string) {
  return caminoGeneratePath({ path: API_ROUTES.newHireJourneys.base, query: queryString });
}

export function v2ApiNewHireJourneyPathById(newHireJourneyId: NewHireJourneyId) {
  return caminoGeneratePath({ path: API_ROUTES.newHireJourneys.show, params: { id: newHireJourneyId || "" } });
}

export function v2ApiNewHireJourneyPath(newHireJourney: NewHireJourney) {
  return v2ApiNewHireJourneyPathById(newHireJourney?.id);
}

export function v2ApiNewHireActionsPathById(newHireJourneyId: NewHireJourneyId) {
  return caminoGeneratePath({ path: API_ROUTES.newHireJourneys.actions, params: { id: newHireJourneyId || "" } });
}

export function v2ApiNewHireActionsPath(newHireJourney: NewHireJourney) {
  return v2ApiNewHireActionsPathById(newHireJourney?.id);
}

export function v2ApiNewHireTasksPathById(newHireJourneyId: NewHireJourneyId) {
  return caminoGeneratePath({ path: API_ROUTES.newHireJourneys.tasks, params: { id: newHireJourneyId || "" } });
}

export function v2ApiNewHireEmailsPathById(newHireJourneyId: NewHireJourneyId) {
  return caminoGeneratePath({ path: API_ROUTES.newHireJourneys.emails, params: { id: newHireJourneyId || "" } });
}

export function v2ApiNewHireMeetingsPathById(newHireJourneyId: NewHireJourneyId) {
  return caminoGeneratePath({ path: API_ROUTES.newHireJourneys.meetings, params: { id: newHireJourneyId || "" } });
}

export function v2ApiNewHireMessagesPathById(newHireJourneyId: NewHireJourneyId) {
  return caminoGeneratePath({ path: API_ROUTES.newHireJourneys.messages, params: { id: newHireJourneyId || "" } });
}

export function v2ApiNewHireTasksPath(newHireJourney: NewHireJourney) {
  return v2ApiNewHireActionsPathById(newHireJourney?.id);
}

// New Hire Journey Issues

export function v2ApiNewHireJourneyIssuesPathById(newHireJourneyId: NewHireJourneyId) {
  return generatePath(API_ROUTES.newHireJourneys.issues, { id: newHireJourneyId || "" });
}

export function v2ApiPendingNewHireJourneyIssuesPathById(newHireJourneyId: NewHireJourneyId) {
  return generatePath(API_ROUTES.newHireJourneys.pendingIssues, { id: newHireJourneyId || "" });
}

// paths

export function apiNewHirePathsPathByNewHireId(newHireId: string) {
  return caminoGeneratePath({ path: API_ROUTES.newHireJourneys.paths.base, params: { id: newHireId || "" } });
}

export function v2ApiPathPathById(pathId: string | undefined) {
  return caminoGeneratePath({ path: API_ROUTES.paths.show, params: { id: pathId || "" } });
}

export function v2ApiPathNewHiresPathByPathId(pathId: string) {
  return caminoGeneratePath({ path: API_ROUTES.paths.newHireJourneys, params: { id: pathId || "" } });
}

export function apiAddNewHireToPathPathByPathId(pathId: string) {
  return caminoGeneratePath({ path: API_ROUTES.paths.addNewHire, params: { id: pathId || "" } });
}

export function v2ApiPathTemplatesPathByPathId(pathId: string) {
  return caminoGeneratePath({ path: API_ROUTES.paths.templates, params: { id: pathId || "" } });
}

export function apiDuplicatePathPathById(pathId: string | undefined) {
  return caminoGeneratePath({ path: API_ROUTES.paths.duplicate, params: { id: pathId || "" } });
}


export function apiAddTemplateToPathPathById(pathId: string | undefined) {
  return caminoGeneratePath({ path: API_ROUTES.paths.addTemplate, params: { id: pathId || "" } });
}

export function apiRemoveTemplateFromPathPathById(pathId: string | undefined) {
  return caminoGeneratePath({ path: API_ROUTES.paths.removeTemplate, params: { id: pathId || "" } });
}

// prebuilt paths

export function apiPrebuiltPathPathById(prebuiltPathId: string) {
  return caminoGeneratePath({ path: API_ROUTES.prebuilt.paths.show, params: { id: prebuiltPathId || "" } });
}

export function apiDuplicatePrebuiltPathPathById(prebuiltPathId: string) {
  return caminoGeneratePath({ path: API_ROUTES.prebuilt.paths.duplicate, params: { id: prebuiltPathId || "" } });
}

export function v2ApiPrebuiltPathTemplatesPathByPrebuiltPathId(prebuiltPathId: string) {
  return caminoGeneratePath({ path: API_ROUTES.prebuilt.paths.templates, params: { id: prebuiltPathId || "" } });
}

// issues

export function apiNewHireWithActiveIssuesListPath(queryString?: string) {
  return caminoGeneratePath({ path: API_ROUTES.newHireJourneys.withActiveIssues.base, query: queryString });
}

export function v2ApiIssueActionsPathById(issueId: string) {
  return caminoGeneratePath({ path: API_ROUTES.issues.actions, params: { id: issueId || "" } });
}

export function v2ApiIssueActionsPath(issue: IssueDetail | IssueSummary) {
  return v2ApiNewHireActionsPathById(issue?.id);
}

// forms

export function apiFormPathById(formId: string) {
  return caminoGeneratePath({ path: API_ROUTES.forms.show, params: { id: formId || "" } });
}

// task reminders

export function apiTaskRemindersPathByTaskId(taskId: string | undefined) {
  return caminoGeneratePath({ path: API_ROUTES.tasks.reminders, params: { id: taskId || "" } });
}

export function apiTaskReminderPathById(taskId: string | undefined) {
  return caminoGeneratePath({ path: API_ROUTES.taskReminders.show, params: { id: taskId || "" } });
}
